import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadioButton, Select, Spinner } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ClientService, TListClientNamesResponse } from '/src/api';
import { Box, Text } from '/src/components';
import { useClientStore, useGlobalStore, useUserStore } from '/src/context';
import { errorMessages } from '/src/lib/errors';
import { TClientId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { SelectOption } from '/src/lib/types';
import { filterSelectOptions, pxToRem } from '/src/utils';

export const ClientsDropdown: React.FC = observer(() => {
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const clientStore = useClientStore();
  const navigate = useNavigate();

  const [clientNames, setClientNames] = useState<TListClientNamesResponse>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingNames, setIsLoadingNames] = useState(false);

  const maxVisibleItems = useRef(10);

  const selectedClientId = useMemo(() => {
    return !clientStore.selectedClientId && userStore.isInternalUser ? 0 : clientStore.selectedClientId;
  }, [userStore.isInternalUser, clientStore.selectedClientId]);

  const clientOptions = useMemo(() => {
    const options = userStore.isInternalUser ? [clientStore.allClientsOption] : [];

    const filtered = filterSelectOptions<number>(searchQuery, [
      ...options,
      ...clientNames.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    ]);

    return isLoadingNames ? [...filtered, { label: '', value: -1 }] : filtered;
  }, [userStore.isInternalUser, clientNames, searchQuery, isLoadingNames]);

  const hasFetchedNamesRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchClientNames = () => {
    hasFetchedNamesRef.current = true;
    setIsLoadingNames(true);
    ClientService.listClientNames()
      .then((names) => {
        if (isMountedRef.current) setClientNames(names);
      })
      .catch((err) => {
        hasFetchedNamesRef.current = false;
        if (isMountedRef.current) globalStore.handleApiError(err, toastMessages.listClients.error);
      })
      .finally(() => {
        if (isMountedRef.current) setIsLoadingNames(false);
      });
  };

  useEffect(() => {
    if (!clientStore.selectedClientId) {
      clientStore.setSelectedClientId(selectedClientId);
    }
  }, [clientStore.selectedClientId]);

  useEffect(() => {
    if (!selectedClientId || selectedClientId === 0) return;
    if (clientStore.selectedClient?.id === selectedClientId) return;

    let ignore = false;
    ClientService.get({ id: selectedClientId })
      .then((client) => {
        if (!ignore) clientStore.setSelectedClient(client);
      })
      .catch((err) => globalStore.handleApiError(err, toastMessages.fetchClient.error));

    return () => {
      ignore = true;
    };
  }, [selectedClientId]);

  const selectedClientName = useMemo(() => {
    if (selectedClientId === 0) return clientStore.allClientsOption.label;
    const fromList = clientNames.find((client) => client.id === selectedClientId)?.name;
    if (fromList) return fromList;
    const cached = clientStore.selectedClient;
    if (cached && cached.id === selectedClientId) return cached.name;
    return undefined;
  }, [selectedClientId, clientNames, clientStore.selectedClient]);

  return (
    <Box justify="center" animation={{ type: 'slideLeft', duration: 500 }}>
      <Select
        name="selected_client"
        plain
        dropHeight={
          clientOptions.length < maxVisibleItems.current ? '100%' : pxToRem(60 + 34.5 * maxVisibleItems.current)
        }
        value={selectedClientId}
        labelKey="label"
        valueKey="value"
        dropProps={{ width: '20rem', round: '6px', border: { size: 'small', color: 'light-5' } }}
        valueLabel={
          <Text alignSelf="center" size="1rem">
            {selectedClientName}
          </Text>
        }
        dropAlign={{
          top: 'bottom',
          right: 'right',
        }}
        onOpen={() => {
          if (!hasFetchedNamesRef.current) fetchClientNames();
        }}
        onClose={() => setSearchQuery('')}
        defaultValue={selectedClientId}
        searchPlaceholder="Filter by Company Name..."
        emptySearchMessage={errorMessages.clientNameSearchNoResult}
        options={clientOptions}
        onChange={({ value }) => {
          if (value.value === -1) return;
          clientStore.setSelectedClientId(value.value);
          if (value.value === 0) {
            navigate('/');
          } else {
            navigate(`/?view&client=${value.value}`, { replace: true });
          }
        }}
        onSearch={setSearchQuery}
      >
        {(option: SelectOption<TClientId>) =>
          option.value === -1 ? (
            <Box align="center" justify="center" pad="small">
              <Spinner />
            </Box>
          ) : (
            <Box
              direction="row"
              gap="0.5rem"
              pad={{
                vertical: 'xsmall',
                horizontal: 'small',
              }}
            >
              <RadioButton name={option.label} checked={selectedClientId === option.value} readOnly />
              <Text size="1rem" color="dark-2" truncate={true} lineHeight="1.40625rem">
                {option.label}
              </Text>
            </Box>
          )
        }
      </Select>
    </Box>
  );
});
